import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import i18n from "@/plugins/i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { templatesApi } from "../infrastructure/api/templates-api";
import { TemplateStatus } from "../domain/types/template";
import { enabledTemplateChannels } from "../domain/templateChannels";
export const useTemplatesStore = defineStore("templates", () => {
    // ── Single-template state (editor) ──
    const currentTemplate = ref(null);
    const loading = ref(false);
    /**
     * Fatal load error: TemplateEditorView renders it INSTEAD of the Unlayer canvas, so only `load`
     * writes it. A mutation must reject to its caller and be toasted there — writing this ref from a
     * mutation unmounts a perfectly usable editor (OP-7087). Named like its `listError` /
     * `versionsError` siblings so the scope is visible at every call site.
     */
    const loadError = ref(null);
    // ── List state ──
    const templates = ref([]);
    const pagination = reactive({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
    });
    const filters = reactive({});
    /** Distinct languages of the tenant's templates, feeding the library language filter. */
    const availableLanguages = ref([]);
    const listLoading = ref(false);
    const listError = ref(null);
    /** Template count per tab: `all`, plus one entry per enabled channel keyed by its support. */
    const channelCounts = reactive({ all: 0 });
    // ── Version history state ──
    const versions = ref([]);
    const versionsPagination = reactive({
        page: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0,
    });
    const versionsLoading = ref(false);
    const versionsError = ref(null);
    // ── List actions ──
    async function fetchTemplates() {
        listLoading.value = true;
        listError.value = null;
        try {
            const params = {
                page: pagination.page,
                pageSize: pagination.pageSize,
                ...filters,
            };
            const result = await templatesApi.getTemplates(params);
            templates.value = result.items;
            pagination.page = result.page;
            pagination.pageSize = result.pageSize;
            pagination.totalCount = result.totalCount;
            pagination.totalPages = result.totalPages;
        }
        catch {
            listError.value = i18n.global.t("templating.error.loadFailed");
        }
        finally {
            listLoading.value = false;
        }
    }
    function setFilter(key, value) {
        filters[key] = value;
        pagination.page = 1;
        fetchTemplates();
    }
    function setPage(page) {
        pagination.page = page;
        fetchTemplates();
    }
    function setPageSize(pageSize) {
        pagination.pageSize = pageSize;
        pagination.page = 1;
        fetchTemplates();
    }
    function clearFilters() {
        filters.support = undefined;
        filters.status = undefined;
        filters.searchTerm = undefined;
        filters.language = undefined;
        pagination.page = 1;
        fetchTemplates();
    }
    async function fetchAvailableLanguages() {
        try {
            availableLanguages.value = await templatesApi.getTemplateLanguages();
        }
        catch (error) {
            // The library language filter stays empty; no user-facing error.
            logger.error("Failed to load template languages", error);
        }
    }
    async function duplicateTemplate(id) {
        const result = await templatesApi.duplicateTemplate(id);
        await fetchTemplates();
        return result;
    }
    async function deleteTemplate(id) {
        await templatesApi.deleteTemplate(id);
        await fetchTemplates();
    }
    async function fetchChannelCounts() {
        const keys = ["all", ...enabledTemplateChannels().map((channel) => channel.support)];
        const results = await Promise.allSettled(keys.map((key) => templatesApi.getTemplates({ pageSize: 1, support: key === "all" ? undefined : key })));
        results.forEach((result, index) => {
            if (result.status === "fulfilled")
                channelCounts[keys[index]] = result.value.totalCount;
        });
    }
    // ── Single-template actions (editor) ──
    async function create(data) {
        loading.value = true;
        try {
            const template = await templatesApi.createTemplate(data);
            currentTemplate.value = template;
            return template;
        }
        finally {
            loading.value = false;
        }
    }
    async function load(id) {
        loading.value = true;
        loadError.value = null;
        try {
            currentTemplate.value = await templatesApi.getTemplateById(id);
        }
        catch (err) {
            loadError.value = i18n.global.t("templating.error.loadTemplateFailed");
            throw err;
        }
        finally {
            loading.value = false;
        }
    }
    async function saveDesign(id, data) {
        const response = await templatesApi.updateTemplateDesign(id, data);
        if (currentTemplate.value && currentTemplate.value.id === id) {
            currentTemplate.value.jsonDesign = data.unlayerDesign;
            currentTemplate.value.htmlContent = data.htmlContent;
            currentTemplate.value.updatedAt = response.updatedAt;
            currentTemplate.value.latestVersionId = response.versionId;
            currentTemplate.value.latestVersionNumber = response.versionNumber;
            currentTemplate.value.hasUnpublishedDraft = true;
            // The latestThumbnail URL is presigned and short-lived — we don't try to update it here.
            // Subsequent reads (`load(id)` or list refresh) regenerate it from the server.
        }
    }
    async function updateName(id, newName) {
        if (!currentTemplate.value || currentTemplate.value.id !== id)
            return;
        const previousName = currentTemplate.value.name;
        currentTemplate.value.name = newName;
        try {
            const response = await templatesApi.renameTemplate(id, { name: newName });
            if (currentTemplate.value && currentTemplate.value.id === id) {
                currentTemplate.value.updatedAt = response.updatedAt;
            }
        }
        catch (err) {
            // Roll the optimistic rename back, then reject to the caller, which toasts.
            if (currentTemplate.value && currentTemplate.value.id === id) {
                currentTemplate.value.name = previousName;
            }
            throw err;
        }
    }
    // ── Version history actions ──
    async function fetchVersions(templateId, page = 1) {
        versionsLoading.value = true;
        versionsError.value = null;
        try {
            const result = await templatesApi.getTemplateVersions(templateId, page, versionsPagination.pageSize);
            if (page === 1) {
                versions.value = result.items;
            }
            else {
                versions.value = [...versions.value, ...result.items];
            }
            versionsPagination.page = result.page;
            versionsPagination.totalCount = result.totalCount;
            versionsPagination.totalPages = result.totalPages;
        }
        catch {
            versionsError.value = i18n.global.t("templating.error.loadVersionsFailed");
        }
        finally {
            versionsLoading.value = false;
        }
    }
    async function publishTemplate(templateId) {
        // Rejects to the caller, which toasts: a refused publish (e.g. an empty design) leaves the
        // template Draft and the editor fully usable, so it must not reach `loadError` (OP-7087).
        const result = await templatesApi.publishTemplate(templateId);
        if (currentTemplate.value?.id === templateId) {
            currentTemplate.value.status = TemplateStatus.Published;
            currentTemplate.value.publishedVersionId = result.publishedVersionId;
            currentTemplate.value.hasUnpublishedDraft = false;
            currentTemplate.value.updatedAt = result.updatedAt;
        }
        const listItem = templates.value.find((t) => t.id === templateId);
        if (listItem) {
            listItem.status = TemplateStatus.Published;
            listItem.updatedAt = result.updatedAt;
        }
        return result;
    }
    async function restoreVersion(templateId, versionId) {
        await templatesApi.restoreVersion(templateId, versionId);
        // Reload template to get the new version content
        await load(templateId);
        // Refresh version history
        await fetchVersions(templateId);
    }
    async function fetchVersionContent(templateId, versionId) {
        return await templatesApi.getTemplateVersionById(templateId, versionId);
    }
    function $reset() {
        currentTemplate.value = null;
        loading.value = false;
        loadError.value = null;
        templates.value = [];
        pagination.page = 1;
        pagination.pageSize = 10;
        pagination.totalCount = 0;
        pagination.totalPages = 0;
        filters.support = undefined;
        filters.status = undefined;
        filters.searchTerm = undefined;
        filters.language = undefined;
        availableLanguages.value = [];
        listLoading.value = false;
        listError.value = null;
        Object.keys(channelCounts).forEach((key) => delete channelCounts[key]);
        channelCounts.all = 0;
        versions.value = [];
        versionsPagination.page = 1;
        versionsPagination.pageSize = 20;
        versionsPagination.totalCount = 0;
        versionsPagination.totalPages = 0;
        versionsLoading.value = false;
    }
    return {
        // Single-template (editor)
        currentTemplate,
        loading,
        loadError,
        create,
        load,
        saveDesign,
        updateName,
        // List
        templates,
        pagination,
        filters,
        listLoading,
        listError,
        channelCounts,
        availableLanguages,
        fetchAvailableLanguages,
        fetchTemplates,
        setFilter,
        setPage,
        setPageSize,
        clearFilters,
        duplicateTemplate,
        deleteTemplate,
        fetchChannelCounts,
        // Version history
        versions,
        versionsPagination,
        versionsLoading,
        versionsError,
        fetchVersions,
        fetchVersionContent,
        publishTemplate,
        restoreVersion,
        $reset,
    };
});
